import { useGLTF } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import type { Group, Material, Mesh } from 'three';
import { MeshBasicMaterial, TextureLoader } from 'three';
import type { GlobeModelProps } from './GlobeModel.types';

// Type for GLTF model nodes and materials
interface GLTFNode extends Mesh {
  geometry: Mesh['geometry'];
  material: Material;
}

interface GLTFResult {
  nodes: Record<string, GLTFNode>;
  materials: Record<string, Material>;
}

/**
 * GlobeModel Component
 *
 * Animated 3D globe with rotating rings and earth texture.
 * Migrated from legacy Globe.js to Atomic Design with React Three Fiber best practices.
 */
export function GlobeModel(props: GlobeModelProps) {
  const groupRef = useRef<Group>(null);
  const earthRef = useRef<Group>(null);
  const ringRef = useRef<Group>(null);
  const ring1Ref = useRef<Group>(null);
  const ring2Ref = useRef<Group>(null);

  // Load 3D model and materials
  const { nodes, materials } = useGLTF('/objects/globe.glb') as unknown as GLTFResult;

  // Create earth material with night texture
  const earthMaterial = new MeshBasicMaterial({
    map: new TextureLoader().load('/objects/textures/night.jpg'),
  });

  // Animation loop - replaces setInterval from legacy code
  useFrame(() => {
    if (!ringRef.current) return;

    ringRef.current.rotation.y += 0.005;
    if (ring1Ref.current) ring1Ref.current.rotation.x -= 0.005;
    if (ring2Ref.current) ring2Ref.current.rotation.x += 0.005;
  });

  // Get nodes - try to access by common GLTF names or fallback to array index
  const getNode = (possibleNames: string[], fallbackIndex: number): GLTFNode | undefined => {
    for (const name of possibleNames) {
      if (nodes[name]) return nodes[name];
    }
    return Object.values(nodes)[fallbackIndex];
  };

  const ring1Node = getNode(['Tube_1_����������_0', 'Tube_1', 'Ring1'], 0);
  const earthNode = getNode(['����������_Material002_0', 'Earth', 'Sphere'], 1);
  const sphereNode = getNode(['Sphere_Mat1_0', 'Sphere'], 2);
  const ring2Node = getNode(['Tube_����������_0', 'Tube', 'Ring2'], 3);

  return (
    <group ref={groupRef} {...props} dispose={null} position={props.position || [-0.5, -2.5, 1.5]}>
      <group rotation={[-Math.PI / 2, 0, 0]}>
        <group rotation={[Math.PI / 2, 0, 0]} scale={0.01}>
          {/* Ring 1 - Top rotating ring */}
          {ring1Node && (
            <group ref={ring1Ref} position={[-37.06, 149.76, -53.95]} rotation={[0.17, -0.64, -0.32]}>
              <mesh geometry={ring1Node.geometry} material={ring1Node.material} />
            </group>
          )}

          {/* Earth globe with night texture */}
          {earthNode && (
            <group ref={earthRef} position={[-36.88, -46.73, -54.47]} rotation={[-Math.PI / 2, 0, -3.05]} scale={90}>
              <mesh geometry={earthNode.geometry} material={earthMaterial} />
            </group>
          )}

          {/* Main rotating sphere - geodesic wireframe */}
          {sphereNode && (
            <group
              ref={ringRef}
              name="Sphere"
              position={[-36.88, -46.56, -54.47]}
              rotation={[Math.PI, 1.09, -Math.PI]}
              scale={1.1}
            >
              <mesh geometry={sphereNode.geometry} material={materials['Mat.1']} />
            </group>
          )}

          {/* Decorative group */}
          <group position={[498.08, 222.94, 444.85]} rotation={[Math.PI, 0.76, 2.97]} />

          {/* Ring 2 - Bottom rotating ring */}
          {ring2Node && (
            <group ref={ring2Ref} position={[-37.06, 149.76, -53.95]} rotation={[1, -0.82, 0.84]}>
              <mesh geometry={ring2Node.geometry} material={ring2Node.material} />
            </group>
          )}
        </group>
      </group>
    </group>
  );
}

// Preload the 3D model for better performance
useGLTF.preload('/objects/globe.glb');
